'use client'

import { cn } from '@/utilities/ui'
import React from 'react'
import RichText from '@/components/RichText'
import { useSubBrand } from '@/providers/SubBrand'
import { getBlockPadding } from '@/utilities/getBlockPadding'

import type { ContentBlock as ContentBlockProps } from '@/payload-types'

// Type-safe helper to get column span class
function getColumnSpanClass(size: NonNullable<ContentBlockProps['columns']>[0]['size']) {
  const colsSpanClasses = {
    full: '12',
    half: '6',
    oneThird: '4',
    twoThirds: '8',
  } as const

  if (!size || !(size in colsSpanClasses)) {
    return '4' // Default fallback
  }
  return colsSpanClasses[size]
}

type Props = ContentBlockProps & {
  disableInnerContainer?: boolean
}

export const ContentBlock: React.FC<Props> = (props) => {
  const { columns, backgroundColor, enableBackground, paddingTop, paddingBottom } = props
  const { subBrand } = useSubBrand()

  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })

  // Determine effective background setting with backwards compatibility
  // Priority: backgroundColor (if explicitly set) > enableBackground (old) > none
  const getEffectiveBackground = () => {
    // If backgroundColor is explicitly set (including 'none'), use it
    if (backgroundColor !== undefined && backgroundColor !== null) {
      return backgroundColor
    }
    // Only fallback to enableBackground if backgroundColor is null/undefined
    if (enableBackground) {
      return 'theme-dark' // Map old enableBackground: true to theme-dark
    }
    return 'none'
  }

  const effectiveBackground = getEffectiveBackground()

  // Background color and styles based on effective selection
  const defaultBgStyles = { backgroundColor: undefined as string | undefined, textColor: '' }
  const backgroundStyles: Record<string, { backgroundColor: string; textColor: string }> = {
    lime:          { backgroundColor: '#00FF6F', textColor: 'text-black' },
    beige:         { backgroundColor: '#D1CCBE', textColor: 'text-black' },
    'theme-light': { backgroundColor: 'var(--brand-color-light)', textColor: 'text-black' },
    'theme-dark':  { backgroundColor: 'var(--brand-color-dark)', textColor: subBrand === 'public' ? 'text-black' : 'text-white' },
    black:         { backgroundColor: '#000000', textColor: 'text-white' },
  }

  const { backgroundColor: bgColor, textColor } = backgroundStyles[effectiveBackground] ?? defaultBgStyles
  const hasBackground = effectiveBackground !== 'none'

  return (
    <div className={paddingClasses}>
      <div
        className={cn('p-8 md:p-12', {
          'rounded-[16px]': hasBackground,
          [textColor]: hasBackground,
        })}
        style={hasBackground ? { backgroundColor: bgColor } : {}}
      >
        <div className="grid grid-cols-4 lg:grid-cols-12 gap-y-8 gap-x-16">
          {columns &&
            columns.length > 0 &&
            columns.map((col, index) => {
              const { richText, size } = col

              const columnSpan = getColumnSpanClass(size)

              return (
                <div
                  className={cn(`col-span-4 lg:col-span-${columnSpan}`, {
                    'md:col-span-2': size !== 'full',
                  })}
                  key={index}
                >
                  {richText && <RichText data={richText} enableGutter={false} />}
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}